import React, { useEffect } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { isLocalStorageEnabled } from '../services/caching/index.tsx';

import { useExecuteMSRNudge } from '../services/msr/index.tsx';
import { useJpdCrossJoinNudgeEligibility } from './eligibility/jpd-eligibility.tsx';
import {
	useConfluenceCrossJoinNudgeEligibility,
	useHasSeenConfLlm,
	useLlmConfluenceCrossJoinNudgeEligibility,
} from './eligibility/confluence-eligibility.tsx';
import { useJpdCrossJoinNudge } from './nudges/jpd-nudge.tsx';
import { useConfluenceCrossJoinNudge } from './nudges/confluence-nudge.tsx';
import { getHasBeenEnrolledInMsr, setEnrolledInMsr } from './eligibility/msr-eligibility.tsx';
import {
	useFireFeatureExposedEventOnce,
	useHasSeenNudgesBefore,
} from './eligibility/loom-eligibility.tsx';

/**
 * CrossJoinNudges conditionally renders various prompts based on user and site context.
 * Rendering occurs in a side-effectful way as targeting and eligibility checks are made synchronously at runtime.
 */
export const CrossJoinNudges = () => {
	return (
		<JSErrorBoundary id="JiraCrossJoinNudges" teamName="groot" packageName="jiraCrossJoinNudges">
			<CrossJoinNudgesWithoutErrorBoundary />
		</JSErrorBoundary>
	);
};

/**
 * useCrossJoinNudges
 * @param execNudge is a temporary prop used to safely feature-gate the gate groot-1154-cjn-global-home
 * @deprecated use CrossJoinNudges instead
 */
export const useCrossJoinNudges = (execNudge = true) => {
	const getJpdCrossJoinNudgeEligibility = useJpdCrossJoinNudgeEligibility();
	const getConfluenceCrossJoinNudgeEligibility = useConfluenceCrossJoinNudgeEligibility();
	const showJpdNudge = useJpdCrossJoinNudge();
	const showConfluenceNudge = useConfluenceCrossJoinNudge();
	const getLlmConfEligibility = useLlmConfluenceCrossJoinNudgeEligibility();
	const getHasSeenConfLlm = useHasSeenConfLlm();
	const getHasSeenNudgesBefore = useHasSeenNudgesBefore();
	const fireFeatureExposedEvent = useFireFeatureExposedEventOnce();

	const executeMSRNudge = useExecuteMSRNudge();

	useEffect(() => {
		async function executeNudges({
			allowLoom = true,
			allowLlm = true,
		}: {
			allowLoom: boolean;
			allowLlm: boolean;
		}) {
			if (allowLoom) {
				fireFeatureExposedEvent({
					cohort: expVal('a_a_test_jira_loom_cjn', 'cohort', 'not-enrolled'),
					flagKey: 'a_a_test_jira_loom_cjn',
				});
			}

			const jpdEligibilityResult = await getJpdCrossJoinNudgeEligibility();
			if (jpdEligibilityResult.isEligible) {
				showJpdNudge();
				return;
			}

			const confluenceEligibilityResult = await getConfluenceCrossJoinNudgeEligibility();
			if (confluenceEligibilityResult.isEligible) {
				showConfluenceNudge(confluenceEligibilityResult.data, allowLlm);
			}
		}

		async function executeNudgesWithMsr() {
			await setEnrolledInMsr();

			// TODO: add more enrolment criteria here (locale etc)
			if (expValEquals('twcg-437-cjn-msr-m1', 'cohort', 'variation', 'not-enrolled')) {
				executeMSRNudge();
			} else {
				executeNudges({ allowLoom: false, allowLlm: false });
			}
		}

		async function orchestrateNudges() {
			if (fg('puma-296-cjn-orchestrator')) {
				const hasLocalStorage = await isLocalStorageEnabled();
				if (!hasLocalStorage) {
					return;
				}

				const enrolledInMsr = await getHasBeenEnrolledInMsr();
				// User already belongs to MSR control/variation
				if (enrolledInMsr) {
					executeNudgesWithMsr();
					return;
				}

				const hasSeenLlm = await getHasSeenConfLlm();
				// User already has seen LLM variation
				if (hasSeenLlm) {
					executeNudges({ allowLoom: false, allowLlm: true });
					return;
				}
				// kill switch to prevent new users or LLM control users from being sent to LLM
				if (fg('puma-296-cjn-orchestrator-llm')) {
					const isLLMEligible = await getLlmConfEligibility();
					// User would belong to LLM control
					if (isLLMEligible.isEligible) {
						executeNudges({ allowLoom: false, allowLlm: true });
						return;
					}
				}

				// User has seen any other nudge so they should not be part of MSR
				// User might be part of Loom variation, so they should not be part of LLM
				const isLoomGroup = await getHasSeenNudgesBefore();
				if (isLoomGroup) {
					executeNudges({ allowLoom: true, allowLlm: false });
					return;
				}

				// User doesn't belong in any other experiment so they should be sent to MSR control/variation
				executeNudgesWithMsr();
			} else {
				// Default control for non-MSR experiments
				executeNudges({ allowLlm: true, allowLoom: false });
			}
		}

		if (execNudge) {
			orchestrateNudges();
		}
	}, [
		execNudge,
		getConfluenceCrossJoinNudgeEligibility,
		getHasSeenConfLlm,
		getHasSeenNudgesBefore,
		getJpdCrossJoinNudgeEligibility,
		getLlmConfEligibility,
		showConfluenceNudge,
		showJpdNudge,
		executeMSRNudge,
		fireFeatureExposedEvent,
	]);
};

const CrossJoinNudgesWithoutErrorBoundary = () => {
	useCrossJoinNudges(true);

	return null;
};

export default CrossJoinNudges;
